(() => {
  const SELECTORS = {
    root: '[data-gg-pdp]',
    media: '[data-gg-media]',
    thumbnail: '[data-gg-thumbnail]',
    quantity: '[data-gg-quantity]',
    stickyQuantity: '[data-gg-sticky-quantity]',
    variantInput: '[data-gg-variant-input]',
    stickyVariant: '[data-gg-sticky-variant]',
    variantPicker: '[data-gg-variant-picker]',
    price: '[data-gg-price]',
    stickyPrice: '[data-gg-sticky-price]',
    addButton: '[data-gg-add]',
    addLabel: '[data-gg-add-label]',
    waitlist: '[data-gg-waitlist]',
    stickyAdd: '[data-gg-sticky-add]',
    stickyWaitlist: '[data-gg-sticky-waitlist]',
    form: '.gg-pdp__form, .gg-pdp__sticky-form',
    formMessage: '[data-gg-form-message]',
    purchase: '[data-gg-purchase]',
    claims: '.gg-pdp__claims',
    sticky: '[data-gg-sticky]'
  };

  class GlissnPdpComparison extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.frame = this.querySelector('[data-gg-comparison-frame]');
      this.range = this.querySelector('input[type="range"]');

      if (!this.frame || !this.range) return;

      this.update = () => {
        const value = Number(this.range.value);
        const afterValue = 100 - value;

        this.frame.style.setProperty('--gg-position', `${value}%`);

        let valueText = `${value}% before and ${afterValue}% after`;

        if (value === 0) {
          valueText = 'After photo only';
        }

        if (value === 50) {
          valueText = 'Half before and half after';
        }

        if (value === 100) {
          valueText = 'Before photo only';
        }

        this.range.setAttribute('aria-valuetext', valueText);
      };

      this.range.addEventListener('input', this.update);
      this.update();
    }

    disconnectedCallback() {
      this.range?.removeEventListener('input', this.update);
    }
  }

  if (!customElements.get('gg-pdp-comparison')) {
    customElements.define(
      'gg-pdp-comparison',
      GlissnPdpComparison
    );
  }

  class GlissnProductPage {
    constructor(root) {
      this.root = root;

      if (this.root.dataset.ggReady === 'true') return;

      this.root.dataset.ggReady = 'true';

      this.media = [
        ...root.querySelectorAll(SELECTORS.media)
      ];

      this.thumbnails = [
        ...root.querySelectorAll(SELECTORS.thumbnail)
      ];

      this.quantity =
        root.querySelector(SELECTORS.quantity);

      this.stickyQuantity =
        root.querySelector(SELECTORS.stickyQuantity);

      this.variantInput =
        root.querySelector(SELECTORS.variantInput);

      this.stickyVariant =
        root.querySelector(SELECTORS.stickyVariant);

      this.variantPicker =
        root.querySelector(SELECTORS.variantPicker);

      this.price =
        root.querySelector(SELECTORS.price);

      this.stickyPrice =
        root.querySelector(SELECTORS.stickyPrice);

      this.addButton =
        root.querySelector(SELECTORS.addButton);

      this.addLabel =
        root.querySelector(SELECTORS.addLabel);

      this.waitlist =
        root.querySelector(SELECTORS.waitlist);

      this.stickyAdd =
        root.querySelector(SELECTORS.stickyAdd);

      this.stickyWaitlist =
        root.querySelector(SELECTORS.stickyWaitlist);

      this.forms = [
        ...root.querySelectorAll(SELECTORS.form)
      ];

      this.formMessage =
        root.querySelector(SELECTORS.formMessage);

      this.purchase =
        root.querySelector(SELECTORS.purchase);

      this.claims =
        root.querySelector(SELECTORS.claims);

      this.sticky =
        root.querySelector(SELECTORS.sticky);

      this.available =
        this.addButton
          ? !this.addButton.disabled
          : true;

      this.isSubmitting = false;

      this.onThumbnailClick = (event) =>
        this.selectMedia(
          Number(
            event.currentTarget.dataset.ggThumbnail
          )
        );

      this.onQuantityClick = (event) =>
        this.changeQuantity(
          Number(
            event.currentTarget.dataset.ggQuantityChange
          )
        );

      this.onQuantityInput = () =>
        this.syncQuantity();

      this.onVariantChange = () =>
        this.syncVariant();

      this.onFormSubmit = (event) => {
        if (this.isSubmitting) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        this.setSubmitting(true);
      };

      this.onCartUpdate = (event) =>
        this.handleCartUpdate(event);

      this.onCartError = (event) =>
        this.handleCartError(event);

      this.onViewportChange = () => {
        if (this.stickyFrame) return;

        this.stickyFrame =
          window.requestAnimationFrame(() => {
            this.stickyFrame = null;
            this.updateSticky();
          });
      };

      this.bind();
      this.syncQuantity();
      this.syncVariant(false);
      this.updateSticky();
    }

    bind() {
      this.thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener(
          'click',
          this.onThumbnailClick
        );
      });

      this.root
        .querySelectorAll(
          '[data-gg-quantity-change]'
        )
        .forEach((button) => {
          button.addEventListener(
            'click',
            this.onQuantityClick
          );
        });

      this.quantity?.addEventListener(
        'change',
        this.onQuantityInput
      );

      this.quantity?.addEventListener(
        'input',
        this.onQuantityInput
      );

      this.variantPicker?.addEventListener(
        'change',
        this.onVariantChange
      );

      this.forms.forEach((form) => {
        form.addEventListener(
          'submit',
          this.onFormSubmit
        );
      });

      this.root.addEventListener(
        'cart:update',
        this.onCartUpdate
      );

      this.root.addEventListener(
        'cart:error',
        this.onCartError
      );

      window.addEventListener(
        'scroll',
        this.onViewportChange,
        { passive: true }
      );

      window.addEventListener(
        'resize',
        this.onViewportChange,
        { passive: true }
      );
    }

    selectMedia(index) {
      if (!Number.isInteger(index)) return;

      this.media.forEach((item) => {
        const active =
          Number(item.dataset.ggMedia) === index;

        item.hidden = !active;

        item.classList.toggle(
          'is-active',
          active
        );
      });

      this.thumbnails.forEach((button) => {
        const active =
          Number(
            button.dataset.ggThumbnail
          ) === index;

        button.classList.toggle(
          'is-active',
          active
        );

        button.setAttribute(
          'aria-pressed',
          String(active)
        );
      });
    }

    changeQuantity(delta) {
      if (
        !this.quantity ||
        !Number.isFinite(delta)
      ) {
        return;
      }

      const current = Math.max(
        1,
        Number.parseInt(
          this.quantity.value,
          10
        ) || 1
      );

      this.quantity.value = String(
        Math.max(
          1,
          current + delta
        )
      );

      this.syncQuantity();
    }

    syncQuantity() {
      if (!this.quantity) return;

      const nextQuantity = Math.max(
        1,
        Number.parseInt(
          this.quantity.value,
          10
        ) || 1
      );

      this.quantity.value =
        String(nextQuantity);

      if (this.stickyQuantity) {
        this.stickyQuantity.value =
          String(nextQuantity);
      }
    }

    syncVariant(updateUrl = true) {
      if (!this.variantPicker) {
        this.available =
          this.addButton
            ? !this.addButton.disabled
            : true;

        return;
      }

      const option =
        this.variantPicker.selectedOptions[0];

      if (!option) return;

      const variantId =
        option.value;

      const available =
        option.dataset.available === 'true';

      const variantPrice =
        option.dataset.price || '';

      this.available = available;

      if (this.variantInput) {
        this.variantInput.value =
          variantId;
      }

      if (this.stickyVariant) {
        this.stickyVariant.value =
          variantId;
      }

      if (
        this.price &&
        variantPrice
      ) {
        this.price.textContent =
          variantPrice;
      }

      if (
        this.stickyPrice &&
        variantPrice
      ) {
        this.stickyPrice.textContent =
          variantPrice;
      }

      if (this.addButton) {
        this.addButton.disabled =
          !available;

        this.addButton.hidden =
          !available;
      }

      if (this.waitlist) {
        this.waitlist.hidden =
          available;
      }

      if (this.stickyAdd) {
        this.stickyAdd.disabled =
          !available;

        this.stickyAdd.hidden =
          !available;
      }

      if (this.stickyWaitlist) {
        this.stickyWaitlist.hidden =
          available;
      }

      if (this.addLabel) {
        this.addLabel.textContent =
          'ADD TO CART';
      }

      if (this.stickyAdd) {
        this.stickyAdd.textContent =
          'ADD TO CART';
      }

      if (
        updateUrl &&
        window.history?.replaceState
      ) {
        const url =
          new URL(
            window.location.href
          );

        url.searchParams.set(
          'variant',
          variantId
        );

        window.history.replaceState(
          {},
          '',
          url.toString()
        );
      }
    }

    setSubmitting(submitting) {
      this.isSubmitting =
        submitting;

      window.clearTimeout(
        this.submitTimeout
      );

      window.clearTimeout(
        this.feedbackTimeout
      );

      if (
        this.addButton &&
        this.available
      ) {
        this.addButton.disabled =
          submitting;
      }

      if (
        this.stickyAdd &&
        this.available
      ) {
        this.stickyAdd.disabled =
          submitting;
      }

      if (
        this.addLabel &&
        this.available
      ) {
        this.addLabel.textContent =
          submitting
            ? 'ADDING…'
            : 'ADD TO CART';
      }

      if (
        this.stickyAdd &&
        this.available
      ) {
        this.stickyAdd.textContent =
          submitting
            ? 'ADDING…'
            : 'ADD TO CART';
      }

      if (submitting) {
        this.showMessage(
          'Adding to cart…'
        );

        this.submitTimeout =
          window.setTimeout(() => {
            this.setSubmitting(false);

            this.showMessage(
              'Please try adding the product again.',
              true
            );
          }, 15000);
      }
    }

    handleCartUpdate(event) {
      if (
        event.detail?.data?.source !==
        'product-form-component'
      ) {
        return;
      }

      if (
        event.detail?.data?.didError
      ) {
        this.handleCartError(event);
        return;
      }

      this.setSubmitting(false);

      if (
        this.addLabel &&
        this.available
      ) {
        this.addLabel.textContent =
          'ADDED ✓';
      }

      if (
        this.stickyAdd &&
        this.available
      ) {
        this.stickyAdd.textContent =
          'ADDED ✓';
      }

      this.showMessage(
        'Added to cart.'
      );

      this.feedbackTimeout =
        window.setTimeout(() => {
          if (
            this.addLabel &&
            this.available
          ) {
            this.addLabel.textContent =
              'ADD TO CART';
          }

          if (
            this.stickyAdd &&
            this.available
          ) {
            this.stickyAdd.textContent =
              'ADD TO CART';
          }

          if (this.formMessage) {
            this.formMessage.hidden =
              true;
          }
        }, 1800);
    }

    handleCartError(event) {
      this.setSubmitting(false);

      const message =
        event.detail?.data?.message ||
        'This item could not be added. Please try again.';

      this.showMessage(
        message,
        true
      );
    }

    showMessage(
      message,
      isError = false
    ) {
      if (!this.formMessage) return;

      this.formMessage.textContent =
        message;

      this.formMessage.hidden =
        false;

      this.formMessage.dataset.error =
        String(isError);
    }

    updateSticky() {
      if (
        !this.sticky ||
        !this.purchase
      ) {
        return;
      }

      if (
        !window
          .matchMedia(
            '(max-width: 989px)'
          )
          .matches
      ) {
        this.sticky.hidden = true;
        return;
      }

      const purchaseHasPassed =
        this.purchase
          .getBoundingClientRect()
          .bottom <= 0;

      const endOfProductHasArrived =
        this.claims
          ? this.claims
              .getBoundingClientRect()
              .top <=
            window.innerHeight * 0.9
          : false;

      this.sticky.hidden =
        !(
          purchaseHasPassed &&
          !endOfProductHasArrived
        );
    }

    destroy() {
      this.thumbnails.forEach(
        (thumbnail) => {
          thumbnail.removeEventListener(
            'click',
            this.onThumbnailClick
          );
        }
      );

      this.root
        .querySelectorAll(
          '[data-gg-quantity-change]'
        )
        .forEach((button) => {
          button.removeEventListener(
            'click',
            this.onQuantityClick
          );
        });

      this.quantity?.removeEventListener(
        'change',
        this.onQuantityInput
      );

      this.quantity?.removeEventListener(
        'input',
        this.onQuantityInput
      );

      this.variantPicker?.removeEventListener(
        'change',
        this.onVariantChange
      );

      this.forms.forEach((form) => {
        form.removeEventListener(
          'submit',
          this.onFormSubmit
        );
      });

      this.root.removeEventListener(
        'cart:update',
        this.onCartUpdate
      );

      this.root.removeEventListener(
        'cart:error',
        this.onCartError
      );

      window.removeEventListener(
        'scroll',
        this.onViewportChange
      );

      window.removeEventListener(
        'resize',
        this.onViewportChange
      );

      if (this.stickyFrame) {
        window.cancelAnimationFrame(
          this.stickyFrame
        );
      }

      window.clearTimeout(
        this.submitTimeout
      );

      window.clearTimeout(
        this.feedbackTimeout
      );

      delete this.root.dataset.ggReady;
    }
  }

  const instances =
    new WeakMap();

  const initialize = (
    scope = document
  ) => {
    scope
      .querySelectorAll(
        SELECTORS.root
      )
      .forEach((root) => {
        if (
          instances.has(root)
        ) {
          return;
        }

        instances.set(
          root,
          new GlissnProductPage(root)
        );
      });
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialize()
    );
  } else {
    initialize();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) =>
      initialize(event.target)
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const root =
        event.target.querySelector(
          SELECTORS.root
        );

      const instance =
        root
          ? instances.get(root)
          : null;

      instance?.destroy();

      if (root) {
        instances.delete(root);
      }
    }
  );
})();